"use client";

import React, { useContext, useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";

// import "./DynamicCarousel.css";

import { AppContext } from "../../context/AppContext";
import { GameContext } from "../../context/GameContext";

interface SlideData {
  id: string;
  imageUrl: string;
  link: string;
  alt: string;
}

type FeaturedGame = {
  gameId: number;
  machineId: number;
  displayName: string;
  created?: string | number | Date;
  categoryIds?: number[];
  cdkSquareImageUrl?: string;
  cdkTallImageUrl?: string;
  gameImageUrl?: string;
  promoImageUrl?: string;
};

import carouselData from "../../data/promotions.json";
const slides: SlideData[] = carouselData;

const DynamicCarousel: React.FC = () => {
  const { user, setIsClosed, games = [], gamesCategories = [] } = useContext(AppContext);
  const { startGame } = useContext(GameContext);
  const router = useRouter();

  const fallbackNewGame: FeaturedGame = {
    gameId: 18,
    machineId: 477,
    displayName: "Coyote Cash 2",
    gameImageUrl: "/assets/new-game/goat-spins-web-ngr-coyote-instant-withdrawals.png",
  };

  const formatGameSlug = (name: string) =>
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const newestGame = useMemo<FeaturedGame | null>(() => {
    const newGamesCategory = gamesCategories.find(
      (category: { path?: string; categoryId?: number }) =>
        category?.path === "new-games"
    );

    const newGamesCategoryId = newGamesCategory?.categoryId;

    const fromNewGames = Array.isArray(games)
      ? (games as FeaturedGame[]).filter((game) =>
          newGamesCategoryId != null
            ? game?.categoryIds?.includes(newGamesCategoryId)
            : true
        )
      : [];

    const sortedByCreated = [...fromNewGames].sort(
      (a, b) =>
        new Date(b?.created || 0).getTime() -
        new Date(a?.created || 0).getTime()
    );

    return sortedByCreated[0] || null;
  }, [games, gamesCategories]);

  const featuredGame = newestGame || fallbackNewGame;
  const featuredGameSlug = `/${formatGameSlug(featuredGame.displayName)}`;

  const getFeaturedGameImage = (game: FeaturedGame) =>
    game?.cdkSquareImageUrl ||
    game?.promoImageUrl ||
    game?.gameImageUrl ||
    fallbackNewGame.gameImageUrl ||
    "";

  const featuredGameImage = getFeaturedGameImage(featuredGame);

  const startGameOnClick = (
    gameId: number,
    machineId: number,
    gameName: string,
    link: string
  ) => {
    if (!user) {
      setIsClosed(false);
      return;
    }
    startGame(gameId, machineId, gameName, () => router.push(link));
  };

  return (
    <div className="dynamic-carousel-wrapper">
      <Swiper
        className="dynamic-carousel-swiper"
        modules={[Pagination, Autoplay]}
        loop
        autoplay={{
          delay: 2500,
          pauseOnMouseEnter: true,
          disableOnInteraction: false,
          reverseDirection: true,
        }}
        grabCursor
        pagination={{ clickable: true }}
        
        breakpoints={{
          0: { direction: "horizontal", slidesPerView: 1, spaceBetween: 12 },
          900: { direction: "horizontal", slidesPerView: 2, spaceBetween: 20 },
          1200: { direction: "vertical", slidesPerView: 2, spaceBetween: 20 },
        }}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.id}>
            {slide.id === "1" ? (
              <div
                className="slideLink cursor-pointer"
                onClick={() =>
                  startGameOnClick(
                    featuredGame.gameId,
                    featuredGame.machineId,
                    featuredGame.displayName,
                    featuredGameSlug
                  )
                }
              >
                <div className="slideMedia">
                  <div className="newGameCarouselImageLayer">
                    <Image
                      src={featuredGameImage}
                      alt={`New Game ${featuredGame.displayName}`}
                      fill
                      sizes="(max-width: 900px) 100vw, (max-width: 1200px) 50vw, 400px"
                      className="newGameCarouselBackgroundImage"
                      style={{ objectFit: "cover" }}
                      priority
                    />
                  </div>
                  <div className="newGameCarouselOverlay" />
                  <div className="newGameCarouselContent">
                    <div className="newGameCarouselText">
                      <p className="newGameCarouselLabel">New Game</p>
                      <div className="newGameCarouselButton">Play Now!</div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <Link
                href={slide.link}
                className="slideLink"
                title={slide.alt}
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className="slideMedia">
                  <Image
                    src={slide.imageUrl}
                    alt={slide.alt}
                    fill
                    sizes="(max-width: 900px) 100vw, (max-width: 1200px) 50vw, 400px"
                    style={{ objectFit: "cover" }}
                  />
                </div>
              </Link>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default DynamicCarousel;
